import React, { useCallback, useEffect, useRef, useState } from 'react'
import { RootState, useAppSelector } from '../../../store'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  WelcomerState,
  clearWelcomerState,
  setInsurerInfo,
  setOrderId,
  setTotalPrice,
} from '../../../store/welcomerSlice'
import { useSelector } from 'react-redux'
import { AuthState } from '../../../store/authSlice'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import styled from 'styled-components'
import Colors from '../../../assets/styles/Colors'
import Responsive from '../../../assets/config/Responsive'
import BaseModal from '../../../components/v2/modals/BaseModal'
import Input from '../../../components/v2/Input'
import Message from '../../../components/Message'
import Button from '../../../components/v2/buttons/Button'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import WelcomerProgressbar from '../../../components/v2/progrees-bars/WelcomerProgressbar'
import MovingPrice from '../../../components/v2/MovingPrice'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import { InsurerInfo, TouristsInfo } from '../../../domain/types'
import api from '../../../api'
import { toast } from 'react-toastify'
import Loader from '../../../components/Loader'
import PhoneNumberInput from '../../../components/v2/PhoneNumberInput'
import { Helmet } from 'react-helmet-async'
import { AppState, clearPhoneInfo } from '../../../store/appSlice'

const WelcomerPolicyRegistration = () => {
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const dispatch = useDispatch()
  //
  const { filter, screenHeader, checkout } = useAppSelector((data: { welcomer: WelcomerState }) => data.welcomer)
  //
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetWelcomerPagesInfoQuery('welcomer-register-policy')
  //
  const pageTitle = pageInfo?.translations?.[lang]?.title
  const stepName = pageInfo?.translations?.[lang]?.stepName
  //
  //
  //
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  //
  const isUserLoggedIn = !!(auth.user?.userType === 'User' && auth.user?.phone && auth.user?.email)
  //
  const [autoRegisterFailed, setAutoRegisterFailed] = useState<boolean>(false)
  //
  const autoRegisterTriggered = useRef<boolean>(false)
  //
  const [openNewPassModal, setOpenNewPassModal] = useState<boolean>(false)
  //
  //
  const [welcomerPolicyRegistration, { data: policyRegistrationResponse, isLoading: isPolicyRegistrationLoading }] =
    api.useWelcomerPolicyRegistrationMutation()
  //
  //
  const _policyRegistration = async (): Promise<boolean> => {
    //
    const smsToNumber = checkout.insurer.smsCode && parseInt(checkout.insurer.smsCode, 10)
    //
    const smsCodeForPayload = isUserLoggedIn ? 0 : smsToNumber || 0
    //
    //
    const promoCode =
      filter.promoCode === '' ? null : filter.discountUsedSuccessfully === false ? null : filter.promoCode
    // for me
    if (checkout.forWho === 'for-me') {
      //
      if (
        (isUserLoggedIn || (smsToNumber && checkout.insurer.smsCode)) &&
        filter &&
        checkout.insurer.email &&
        checkout.insurer.phone &&
        checkout.insurer &&
        filter?.welcomersDate?.[0]?.dateFormat &&
        filter?.offer &&
        filter?.insuranceDays
      ) {
        //
        // insurer info
        const insurerInfoType: InsurerInfo = {
          translations: checkout.insurer.translations!,
          birthday: filter.welcomersDate?.[0]?.dateFormat,
          personalNumber: checkout.insurer.idNumber || null,
          passportNumber: checkout.insurer.passportNumber || null,
          gender: checkout.insurer.gender!,
          email: checkout.insurer.email,
          phone: checkout.insurer.phone,
          citizenship: checkout.insurer.cityzenship!,
          smsCode: smsCodeForPayload,
          password: checkout.insurer.password,
          franiTerms: {
            consentTerm: checkout.insurer.consentTerm!,
            policyTerm: checkout.insurer.policyTerm!,
            privacyTerm: checkout.insurer.privacyTerm!,
          },
        }
        // insurer info in tourists array
        const touristsInfoType: TouristsInfo[] = [
          {
            translations: checkout.insurer.translations!,
            birthday: filter.welcomersDate?.[0]?.dateFormat,
            passportNumber: checkout.insurer.passportNumber || null,
            citizenship: checkout.insurer.cityzenship!,
            personalNumber: checkout.insurer.idNumber || null,
            insuranceDays: filter.insuranceDays,
            gender: checkout.insurer.gender!,
            price: {
              id: filter?.offer?.id,
              limitId: filter?.limitId,
              currencyId: filter?.currencyId,
              welcomerAgeGroupId: filter?.offer?.welcomerAgeGroupId,
              welcomerDayGroupId: filter?.offer?.welcomerDayGroupId,
              premium: filter?.offer?.premium,
              welcomerPackage: {
                id: filter?.offer?.welcomerPackage?.id,
                attachedAddonIds:
                  (filter.attachedAddons &&
                    filter.attachedAddons.length >= 1 &&
                    filter.attachedAddons.map((at) => at.addon.id)) ||
                  [],
                groupedAttachedAddonInfoIds:
                  (filter.groupedAttachedAddons &&
                    filter.groupedAttachedAddons?.length >= 1 &&
                    filter.groupedAttachedAddons.map((grAt) => grAt.addon.id)) ||
                  [],
              },
            },
          },
        ]
        //
        // welcomer policy registration for me
        try {
          await welcomerPolicyRegistration({
            welcomerPackageId: filter?.offer?.welcomerPackage?.id!,
            sgsVerification: checkout.insurer.sgsVerification!,
            betterOffer: filter.betterOffer ?? false,
            startDate: filter.singleDates?.start as string,
            endDate: filter.singleDates?.end as string,
            compensationLimit: filter.offer?.welcomerPackage?.mainPackage?.compensationLimit,
            compensationCurrency:
              filter.offer?.welcomerPackage?.mainPackage?.compensationCurrencies[0].currency.toLowerCase(),
            franchise:
              filter.offer.welcomerPackage.mainPackage.franchises === null
                ? 0
                : filter.offer.welcomerPackage.mainPackage.franchises[0],
            promoCode: promoCode,
            insurerInfo: insurerInfoType,
            touristsInfo: touristsInfoType,
          }).unwrap()
          //
          // toast.success(`${t('policySuccessfullyRegistered')}`)
          //
          return true
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else if (error?.data?.message === 'PROMO_CODE_ERROR') {
            toast.error(`${t('promoCodeCantbeUsed')}`)
          } else {
            toast.error(`${t('anErrorOccurred')}`)
          }
          return false
        }
      }
    }
    //
    //
    //
    if (checkout.forWho === 'for-other') {
      //
      const convertDateFormat = (inputDate?: string) => {
        if (inputDate !== undefined) {
          const parts = inputDate.split(' / ')
          if (parts.length === 3) {
            const day = parts[0]
            const month = parts[1]
            const year = parts[2]
            const formattedDate = `${year}-${month}-${day}`
            return formattedDate
          } else {
            return 'Invalid date format'
          }
        }
      }
      //
      const converteInsurerdDate = convertDateFormat(checkout.insurer.dateOfbirth as unknown as string)
      //
      //
      if (
        (isUserLoggedIn || (smsToNumber && checkout.insurer.smsCode)) &&
        filter &&
        checkout.insurer.email &&
        checkout.insurer.phone &&
        checkout.insurer &&
        filter.offer &&
        converteInsurerdDate &&
        checkout.welcomers &&
        checkout.welcomers.length >= 1 &&
        filter?.insuranceDays
      ) {
        // insurer Info
        const insurerInfoType: InsurerInfo = {
          translations: checkout.insurer.translations!,
          birthday: converteInsurerdDate,
          personalNumber: checkout.insurer.idNumber || null,
          passportNumber: checkout.insurer.passportNumber || null,
          citizenship: checkout.insurer.cityzenship!,
          gender: checkout.insurer.gender!,
          email: checkout.insurer.email,
          phone: checkout.insurer.phone,
          smsCode: smsCodeForPayload,
          password: checkout.insurer.password,
          franiTerms: {
            consentTerm: checkout.insurer.consentTerm!,
            policyTerm: checkout.insurer.policyTerm!,
            privacyTerm: checkout.insurer.privacyTerm!,
          },
        }
        //
        //
        const WelcomersUnion = [...checkout.welcomers]
        //
        //
        //welcomers
        const touristsInfoType = WelcomersUnion?.map(
          ({
            translations,
            cityzenship,
            gender,
            dateOfbirth,
            passportNumber,
            idNumber,
            addons,
            groupedAddonInfos,
          }) => ({
            translations: translations,
            birthday:
              filter.welcomersDate?.find((x) => x.date === (dateOfbirth as any)?.split('-')[0])?.dateFormat ??
              converteInsurerdDate,
            passportNumber: passportNumber || null,
            citizenship: cityzenship,
            personalNumber: idNumber || null,
            insuranceDays: filter?.insuranceDays,
            gender: gender,
            price: {
              id: filter?.welcomersDatePrices?.find((x) => x.date === (dateOfbirth as any)?.split('-')[0])?.priceId,
              limitId: filter?.limitId,
              currencyId: filter?.currencyId,
              welcomerAgeGroupId: filter?.welcomersDatePrices?.find(
                (x) => x.date === (dateOfbirth as any)?.split('-')[0],
              )?.welcomerAgeGroupId,
              welcomerDayGroupId: filter?.welcomersDatePrices?.find(
                (x) => x.date === (dateOfbirth as any)?.split('-')[0],
              )?.welcomerDayGroupId,
              premium: filter?.welcomersDatePrices?.find((x) => x.date === (dateOfbirth as any)?.split('-')[0])
                ?.premium,
              welcomerPackage: {
                id: filter?.offer?.welcomerPackage?.id,
                attachedAddonIds: (addons && addons?.length >= 1 && addons?.map((x) => x.id)) || [],
                groupedAttachedAddonInfoIds:
                  (groupedAddonInfos && groupedAddonInfos?.length >= 1 && groupedAddonInfos?.map((x) => x.id)) || [],
              },
            },
          }),
        )
        //
        //
        //welcomer policy registration & for-other
        try {
          await welcomerPolicyRegistration({
            welcomerPackageId: filter?.offer?.welcomerPackage?.id!,
            sgsVerification: checkout.insurer.sgsVerification!,
            betterOffer: filter.betterOffer ?? false,
            startDate: filter.singleDates?.start as string,
            endDate: filter.singleDates?.end as string,
            compensationLimit: filter.offer?.welcomerPackage?.mainPackage?.compensationLimit,
            compensationCurrency:
              filter.offer?.welcomerPackage?.mainPackage?.compensationCurrencies[0].currency.toLowerCase(),
            franchise:
              filter.offer.welcomerPackage.mainPackage.franchises === null
                ? 0
                : filter.offer.welcomerPackage.mainPackage.franchises[0],
            promoCode: promoCode,
            insurerInfo: insurerInfoType,
            // @ts-ignore
            touristsInfo: touristsInfoType,
          }).unwrap()
          //
          // toast.success(`${t('policySuccessfullyRegistered')}`)
          //
          //
          return true
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error(`${t('anErrorOccurred')}`)
          }
          return false
        }
      }
    }
    //
    //
    // for me and other
    if (checkout.forWho === 'me-and-other') {
      //
      //
      if (
        (isUserLoggedIn || (smsToNumber && checkout.insurer.smsCode)) &&
        filter &&
        checkout.insurer.email &&
        checkout.insurer.phone &&
        checkout.insurer &&
        filter.offer &&
        checkout.welcomers &&
        checkout.welcomers.length >= 1 &&
        filter?.insuranceDays
      ) {
        //
        //
        // insurer Info
        const insurerInfoType: InsurerInfo = {
          translations: checkout.welcomers?.[0]?.translations!,
          birthday: filter.welcomersDate?.find(
            (x) => x.date === (checkout.welcomers?.[0]?.dateOfbirth as any)?.split('-')[0],
          )?.dateFormat as string,
          personalNumber: checkout.welcomers?.[0]?.idNumber || null,
          passportNumber: checkout.welcomers?.[0]?.passportNumber || null,
          citizenship: checkout.insurer.cityzenship!,
          gender: checkout.welcomers?.[0]?.gender!,
          email: checkout.insurer.email,
          phone: checkout.insurer.phone,
          smsCode: smsCodeForPayload,
          password: checkout.insurer.password,
          franiTerms: {
            consentTerm: checkout.welcomers?.[0]?.consentTerm!,
            policyTerm: checkout.insurer.policyTerm!,
            privacyTerm: checkout.insurer.privacyTerm!,
          },
        }
        //
        //
        //welcomers
        const touristsInfoType = checkout.welcomers?.map(
          ({
            translations,
            cityzenship,
            gender,
            dateOfbirth,
            passportNumber,
            idNumber,
            addons,
            groupedAddonInfos,
          }) => ({
            translations: translations,
            birthday: filter.welcomersDate?.find((x) => x.date === (dateOfbirth as any)?.split('-')[0])?.dateFormat,
            passportNumber: passportNumber || null,
            citizenship: cityzenship,
            personalNumber: idNumber || null,
            insuranceDays: filter?.insuranceDays,
            gender: gender,
            price: {
              id: filter?.welcomersDatePrices?.find((x) => x.date === (dateOfbirth as any)?.split('-')[0])?.priceId,
              limitId: filter?.limitId,
              currencyId: filter?.currencyId,
              welcomerAgeGroupId: filter?.welcomersDatePrices?.find(
                (x) => x.date === (dateOfbirth as any)?.split('-')[0],
              )?.welcomerAgeGroupId,
              welcomerDayGroupId: filter?.welcomersDatePrices?.find(
                (x) => x.date === (dateOfbirth as any)?.split('-')[0],
              )?.welcomerDayGroupId,
              premium: filter?.welcomersDatePrices?.find((x) => x.date === (dateOfbirth as any)?.split('-')[0])
                ?.premium,
              welcomerPackage: {
                id: filter?.offer?.welcomerPackage?.id,
                attachedAddonIds: (addons && addons?.length >= 1 && addons?.map((x) => x.id)) || [],
                groupedAttachedAddonInfoIds:
                  (groupedAddonInfos && groupedAddonInfos?.length >= 1 && groupedAddonInfos?.map((x) => x.id)) || [],
              },
            },
          }),
        )
        //
        //
        //welcomer policy registration & me-and-other
        try {
          await welcomerPolicyRegistration({
            welcomerPackageId: filter?.offer?.welcomerPackage?.id!,
            sgsVerification: checkout.insurer.sgsVerification!,
            betterOffer: filter.betterOffer ?? false,
            startDate: filter.singleDates?.start as string,
            endDate: filter.singleDates?.end as string,
            compensationLimit: filter.offer?.welcomerPackage?.mainPackage?.compensationLimit,
            compensationCurrency:
              filter.offer?.welcomerPackage?.mainPackage?.compensationCurrencies[0].currency.toLowerCase(),
            franchise:
              filter.offer.welcomerPackage.mainPackage.franchises === null
                ? 0
                : filter.offer.welcomerPackage.mainPackage.franchises[0],
            promoCode: promoCode,
            insurerInfo: insurerInfoType,
            //@ts-ignore
            touristsInfo: touristsInfoType,
          }).unwrap()
          //
          // toast.success(`${t('policySuccessfullyRegistered')}`)
          //
          //
          return true
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error(`${t('anErrorOccurred')}`)
          }
          return false
        }
      }
    }
    //
    return false
  }
  //
  useEffect(() => {
    if (
      isUserLoggedIn &&
      !autoRegisterTriggered.current &&
      !autoRegisterFailed &&
      checkout.insurer.email &&
      checkout.insurer.phone &&
      !policyRegistrationResponse &&
      !isPolicyRegistrationLoading
    ) {
      autoRegisterTriggered.current = true
      _policyRegistration().then((ok) => {
        if (!ok) {
          setAutoRegisterFailed(true)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn, autoRegisterFailed, checkout.insurer.email, checkout.insurer.phone, policyRegistrationResponse])
  //
  const setOrderInfoCallBack = useCallback(() => {
    if (policyRegistrationResponse && policyRegistrationResponse.id && policyRegistrationResponse.totalPrice !== null) {
      if (Number(policyRegistrationResponse.totalPrice) <= 0) {
        return [dispatch(clearPhoneInfo()), dispatch(clearWelcomerState()), navigate('/pay/payment-success')]
      }
      //
      return [
        dispatch(setOrderId(policyRegistrationResponse.id)),
        dispatch(setTotalPrice(policyRegistrationResponse.totalPrice)),
        navigate('/welcomer-payment'),
        dispatch(clearPhoneInfo()),
      ]
    }
    //
    if (policyRegistrationResponse && !policyRegistrationResponse.id) {
      toast.error(`${t('failedToSaveOrderId')}`)
    }
    //
    if (policyRegistrationResponse && policyRegistrationResponse.totalPrice === null) {
      toast.error(`${t('failedToSaveTotalPrice')}`)
    }
    //
  }, [policyRegistrationResponse, dispatch, navigate, t])
  //
  //
  useEffect(() => {
    setOrderInfoCallBack()
    //
    return () => {}
  }, [setOrderInfoCallBack])
  //
  // send verification sms mutation
  const [verificationSms, { isLoading: isverificationSmsNumberLoading, isSuccess: verificationSmsNumberSuccess }] =
    api.useSendVerificationSmsMutation()
  //
  // authorization after mobile verification
  const [authorizationAfterMobileVerification, { data: authSmsVerificationResponse, error: authSmsVerificationError }] =
    api.useAuthorizationAfterMobileVerificationMutation()
  //
  //
  // send verification sms number
  const sendVerificationSmsFunction = async () => {
    if (checkout.insurer.phone && checkout.insurer.email) {
      try {
        //
        await verificationSms({ phone: checkout.insurer.phone }).unwrap()
        //
        toast.success(`${t('smsSuccessfullySent')}`)
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('unknownError')} PolicyRegistration.tsx`)
        }
      }
    }
  }
  //
  //
  // authorization after mobile verification
  useEffect(() => {
    if (
      checkout.insurer &&
      checkout.insurer.email &&
      checkout.insurer.phone &&
      checkout.insurer.smsCode &&
      checkout.insurer.smsCode.toString().length === 4
    ) {
      //
      const smsCodeToNumber = parseFloat(checkout.insurer.smsCode)
      const checkAuthorization = async () => {
        try {
          //
          await authorizationAfterMobileVerification({
            email: checkout.insurer.email!,
            phone: checkout.insurer.phone!,
            smsCode: smsCodeToNumber,
            userId: auth.user?.id || 0,
          }).unwrap()
          //
        } catch (error: any) {
          //
          // if sms does not match & clear sms field
          if (error?.data?.errorCode === 'SMS_CODE_DOES_NOT_MATCH') {
            toast.info(error?.data?.errorMessage?.[lang])
          }
          //
          //
          // if user noes not exist
          if (error?.data.errorCode === 'USER_DOES_NOT_EXIST') {
            toast.info(error?.data?.errorMessage?.[lang])
          }
          //
          //
          if (
            auth &&
            auth.user &&
            auth.user?.userType === 'User' &&
            auth.user.email === checkout.insurer.email &&
            auth.user.phone === checkout.insurer.phone
          ) {
            return [
              dispatch(
                setInsurerInfo({
                  fieldName: 'userHaveAccount',
                  fieldValue: true,
                }),
              ),
            ]
          }
        }
      }
      //
      checkAuthorization()
      //
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authorizationAfterMobileVerification,
    checkout.insurer.email,
    checkout.insurer.phone,
    checkout.insurer.smsCode,
    auth.user?.id,
  ])
  //
  //
  // open password modal
  useEffect(() => {
    if (
      authSmsVerificationError &&
      // @ts-ignore
      authSmsVerificationError?.data?.errorCode === 'USER_DOES_NOT_EXIST' &&
      (auth.user === null || auth.user) &&
      checkout.insurer.smsCode &&
      checkout.insurer.phone &&
      checkout.insurer.email &&
      checkout.insurer.smsCode.length === 4
    ) {
      setOpenNewPassModal(true)
    }
    //

    return () => {}
  }, [auth.user, authSmsVerificationError, checkout.insurer.smsCode, checkout.insurer.phone, checkout.insurer.email])
  //
  //
  // look logged user data
  const setLoggedUserCallBack = useCallback(() => {
    if (auth.user?.phone && auth?.user?.userType === 'User' && auth.user?.email) {
      return [
        dispatch(
          setInsurerInfo({
            fieldName: 'phone',
            fieldValue: auth.user?.phone,
          }),
        ),
        dispatch(
          setInsurerInfo({
            fieldName: 'email',
            fieldValue: auth.user?.email,
          }),
        ),
      ]
    }
  }, [auth?.user?.userType, auth.user?.phone, auth.user?.email, dispatch])
  //
  // set logged user data in rtk store
  useEffect(() => {
    //
    setLoggedUserCallBack()
    //
    return () => {}
  }, [setLoggedUserCallBack])
  //
  // yup validations schema GE
  const ContactValidationSchema = Yup.object({
    email: Yup.string()
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .email(`${t('enterValidMail')}`)
      .required(`${t('required')}`),
    phone: Yup.string()
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      // .min(9, `${t('enterValidPhone')}`)
      // .max(9, `${t('enterValidPhone')}`)
      .required(`${t('required')}`),
    password: Yup.string()
      .min(8, `${t('minPassLenght')}`)
      .matches(/[0-9]/, `${t('passContainNumber')}`)
      .matches(/[a-z]/, `${t('passContainLowerCase')}`)
      .matches(/[a-zA-Z]/, `${t('passContainEnglishLetters')}`)
      .matches(/[A-Z]/, `${t('passContainUpperCase')}`),
    confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], `${t('enterSamePass')}`),
    smsCode: Yup.string()
      .min(4, `${t('enterFourDigitCode')}`)
      .max(4, `${t('enterFourDigitCode')}`)
      .required(`${t('enterFourDigitCode')}`),
  })
  //
  // new user
  useEffect(() => {
    //
    if (
      authSmsVerificationError &&
      (auth.user === null || auth.user) &&
      checkout.insurer.smsCode &&
      checkout.insurer.phone &&
      checkout.insurer.smsCode.length === 4 &&
      checkout.insurer.email &&
      checkout.insurer.password
    ) {
      const new_user = { email: checkout.insurer.email, password: checkout.insurer.password }
      //
      localStorage.setItem('new_user', JSON.stringify(new_user))
    }
    //
    return () => {}
  }, [
    checkout.insurer.password,
    checkout.insurer.email,
    checkout.insurer.phone,
    checkout.insurer.userHaveAccount,
    authSmsVerificationError,
    auth.user,
    checkout.insurer.smsCode,
  ])
  //
  //
  const MainValidationSchema = ContactValidationSchema
  //
  //
  // initial values
  const initialValues = {
    email: checkout.insurer.email || '',
    phone: checkout.insurer.phone || '',
    password: checkout.insurer.password || '',
    confirmPassword: checkout.insurer.confirmPassword || '',
    smsCode: checkout.insurer.smsCode || '',
  }
  // formi state
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: MainValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log(values)
    },
  })
  //
  //
  // auto navigate first page
  useEffect(() => {
    if (!filter.singleDates?.start && !policyRegistrationResponse?.id) {
      navigate('/welcomer-start-date')
    }
    //
    return () => {}
  }, [filter.singleDates?.start, navigate, policyRegistrationResponse?.id])
  //
  //
  const MainLoading = isPolicyRegistrationLoading || isPageInfoLoading || (isUserLoggedIn && !autoRegisterFailed)
  //

  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageTitle} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <BaseModal isOpen={openNewPassModal} onClose={() => setOpenNewPassModal(!openNewPassModal)}>
        <NewAccountModalBlock>
          <h5>{t('simpleRegistration')}</h5>
          <p>{t('createYourAccount')}</p>
          <Input
            id="welcomer-policy-registration-password"
            name="welcomer-policy-registration-password"
            label={t('enterNewPassword')}
            type="password"
            icon={true}
            placeholder=""
            value={checkout.insurer.password || ''}
            onChange={(value) => [
              dispatch(
                setInsurerInfo({
                  fieldName: 'password',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('password', value),
            ]}
            width={'fullWidth'}
            error={formik.errors.password && checkout.insurer.password ? true : false}
            errorText={formik.errors.password}
            onBlur={formik.handleBlur}
            style={{
              marginTop: '24px',
            }}
          />

          <Input
            id="welcomer-policy-confirm-registration-password"
            name="welcomer-policy-confirm-registration-password"
            label={t('repeatNewPassword')}
            type="password"
            icon={true}
            placeholder=""
            value={checkout.insurer.confirmPassword || ''}
            onChange={(value) => [
              dispatch(
                setInsurerInfo({
                  fieldName: 'confirmPassword',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('confirmPassword', value),
            ]}
            width={'fullWidth'}
            error={formik.errors.confirmPassword && checkout.insurer.confirmPassword ? true : false}
            errorText={formik.errors.confirmPassword}
            onBlur={formik.handleBlur}
            style={{
              marginTop: '24px',
            }}
          />

          <Message
            style={{
              marginTop: '10px',
              marginBottom: '0px',
            }}
            variant="gray"
            text={`${t('passwordText')}`}
          />

          <Button
            fullWidth={true}
            style={{
              marginTop: '48px',
            }}
            disabled={
              checkout.insurer.password &&
              checkout.insurer.confirmPassword &&
              checkout.insurer.password === checkout.insurer.confirmPassword &&
              checkout.insurer.consentTerm
                ? false
                : true
            }
            buttonType="primary"
            text={t('registrationAndContinue')}
            onClick={() => {
              if (
                checkout.insurer.password &&
                checkout.insurer.confirmPassword &&
                checkout.insurer.password === checkout.insurer.confirmPassword &&
                checkout.insurer.consentTerm
              ) {
                _policyRegistration()
              }
            }}
          />
        </NewAccountModalBlock>
      </BaseModal>
      {/*  */}
      {/*  */}

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />
      <Content>
        <Title text={pageTitle} />

        <WelcomerProgressbar stepName={stepName} />

        <MovingPrice price={filter.offerTotalPrice} />

        <WhiteBox>
          <Input
            id="welcomer-policy-registration-email"
            name="welcomer-policy-registration-email"
            label={`${t('email')}`}
            type="email"
            placeholder=""
            value={checkout.insurer.email || ''}
            onChange={(value) => [
              dispatch(
                setInsurerInfo({
                  fieldName: 'email',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('email', value),
            ]}
            width={'fullWidth'}
            error={formik.errors.email && checkout.insurer.email ? true : false}
            errorText={formik.errors.email}
            onBlur={formik.handleBlur}
            disabled={
              checkout.insurer.email &&
              checkout.insurer.phone &&
              checkout.insurer.smsCode &&
              checkout.insurer.smsCode.length === 4
                ? true
                : false
            }
          />

          <PhoneNumberInput
            style={{
              marginTop: '24px',
            }}
            id="welcomer-policy-registration-phone"
            label={`${t('phone')}`}
            value={checkout.insurer.phone || ''}
            onChange={(value) => [
              dispatch(
                setInsurerInfo({
                  fieldName: 'phone',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('phone', value),
            ]}
            resendOnclick={sendVerificationSmsFunction}
            sendSms={sendVerificationSmsFunction}
            error={formik.errors.phone && checkout.insurer.phone ? true : false}
            errorText={formik.errors.phone}
            onBlur={formik.handleBlur}
            hideButtons={false}
            disabled={
              (checkout.insurer.email &&
                checkout.insurer.phone &&
                checkout.insurer.smsCode &&
                verificationSmsNumberSuccess &&
                checkout.insurer.smsCode.length === 4) ||
              isverificationSmsNumberLoading ||
              (policyRegistrationResponse && policyRegistrationResponse.id && policyRegistrationResponse.totalPrice) ||
              formik.errors.email
                ? true
                : false
            }
          />

          {(verificationSmsNumberSuccess && checkout.insurer.phone && checkout.insurer.email) ||
          (checkout.insurer.phone && checkout.insurer.email && checkout.insurer.smsCode) ? (
            <Input
              id="abroad-policy-registration-phone-sms"
              name="abroad-policy-registration-phone=sms"
              label={`${t('smsCode')}`}
              type="number"
              placeholder=""
              value={checkout.insurer.smsCode || ''}
              onChange={(value) => [
                dispatch(
                  setInsurerInfo({
                    fieldName: 'smsCode',
                    fieldValue: value,
                  }),
                ),
                formik.setFieldValue('smsCode', value),
              ]}
              width={'fullWidth'}
              error={formik.errors.smsCode && checkout.insurer.smsCode ? true : false}
              errorText={formik.errors.smsCode}
              onBlur={formik.handleBlur}
              disabled={
                policyRegistrationResponse && policyRegistrationResponse.id && policyRegistrationResponse.totalPrice
                  ? true
                  : false
              }
              style={{
                marginTop: '24px',
              }}
            />
          ) : null}
        </WhiteBox>
      </Content>

      <Buttons>
        <NavigationButton
          text={`${t('back')}`}
          buttonType="prev"
          onClick={() => navigate('/welcomer-check-information')}
        />

        <NavigationButton
          text={'შემდეგი'}
          buttonType="next"
          disabled={
            (!authSmsVerificationError &&
              checkout.insurer.email &&
              checkout.insurer.phone &&
              checkout.insurer.smsCode &&
              checkout.insurer.smsCode.length === 4) ||
            (authSmsVerificationError &&
              checkout.insurer.email &&
              checkout.insurer.phone &&
              checkout.insurer.smsCode &&
              checkout.insurer.smsCode.length === 4 &&
              checkout.insurer.password &&
              checkout.insurer.confirmPassword &&
              checkout.insurer.password === checkout.insurer.confirmPassword)
              ? false
              : true
          }
          //
          onClick={() => {
            if (
              // if user don't have account & open create password modal & create new user profile
              authSmsVerificationError &&
              (auth.user === null || auth.user) &&
              checkout.insurer.smsCode &&
              checkout.insurer.phone &&
              checkout.insurer.email &&
              checkout.insurer.smsCode.length === 4
            ) {
              setOpenNewPassModal(true)
            }
            //
            //
            if (
              //  if user have account & policy registration
              (authSmsVerificationResponse &&
                authSmsVerificationResponse.id &&
                checkout.insurer.email &&
                checkout.insurer.smsCode) ||
              (checkout.insurer.password &&
                checkout.insurer.smsCode &&
                checkout.insurer.smsCode.length === 4 &&
                formik.isValid)
            ) {
              //
              _policyRegistration()
              //
            }
            //
          }}
        />
      </Buttons>

      <BackgroundIcon />
    </Wrapper>
  )
}
//
const NewAccountModalBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  h5 {
    font-size: 24px;
    color: ${Colors.primaryBlack};
    text-align: center;
  }
  p {
    text-align: center;
    font-size: 14px;
    color: ${Colors.nevadaGrey};
  }

  ${Responsive.mobile} {
    h5 {
      font-size: 18px;
    }
  }
`
const WhiteBox = styled.div`
  background-color: ${Colors.white};
  margin-block: 32px;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  max-width: 464px;
  width: 100%;
  margin-inline: auto;
  border-radius: 8px;
  padding: 48px;

  ${Responsive.mobile} {
    padding: 20px;
  }
`
const Buttons = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  gap: 20px;
  width: min-content;
  margin-top: 48px;
  margin-inline: auto;
  button {
    width: 175px;
  }

  ${Responsive.mobile} {
    margin-top: 40px;
    gap: 16px;
    button {
      width: 140px;
    }
  }
`
const Content = styled.div`
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;

  ${Responsive.mobile} {
    margin-top: 26px;
    width: 100%;
  }
`
const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;
  /*  */
  .base-modal-close-btn {
    display: none;
  }
`

export default WelcomerPolicyRegistration
